import { ColumnsType } from "antd/es/table";

export type ReportCategory =
  | "revenue"
  | "services"
  | "payments"
  | "tax"
  | "analytics"
  | "finance";
export type FilterType =
  | "dateRange"
  | "date"
  | "asOf"
  | "termsDays"
  | "year"
  | "department"
  | "staff"
  | "room"
  | "orderType"
  | "bookingId"
  | "paymentMethod"
  | "weekStart"
  | "month";

export interface ReportDefinition {
  id: string;
  title: string;
  category: ReportCategory;
  categoryLabel: string;
  filters: FilterType[];
  endpoint: string;
  pdfEndpoint: string;
  columns: ColumnsType<any>;
}

export const REPORT_REGISTRY: ReportDefinition[] = [
  // === Revenue & Sales ===
  {
    id: "daily-sales",
    title: "Daily Sales",
    category: "revenue",
    categoryLabel: "Revenue & Sales",
    filters: ["dateRange"],
    endpoint: "/reports/daily-sales",
    pdfEndpoint: "/reports/pdf/daily-sales",
    columns: [],
  },
  {
    id: "restaurant-sales",
    title: "Restaurant Sales",
    category: "revenue",
    categoryLabel: "Revenue & Sales",
    filters: ["dateRange", "orderType"],
    endpoint: "/reports/restaurant-sales",
    pdfEndpoint: "/reports/pdf/restaurant-sales",
    columns: [],
  },
  {
    id: "take-away-sales",
    title: "Take Away Sales",
    category: "revenue",
    categoryLabel: "Revenue & Sales",
    filters: ["dateRange"],
    endpoint: "/reports/take-away-sales",
    pdfEndpoint: "/reports/pdf/take-away-sales",
    columns: [],
  },
  {
    id: "dine-in-sales",
    title: "Dine-In Sales",
    category: "revenue",
    categoryLabel: "Revenue & Sales",
    filters: ["dateRange"],
    endpoint: "/reports/dine-in-sales",
    pdfEndpoint: "/reports/pdf/dine-in-sales",
    columns: [],
  },
  {
    id: "room-service-sales",
    title: "Room Service Sales",
    category: "revenue",
    categoryLabel: "Revenue & Sales",
    filters: ["dateRange", "room"],
    endpoint: "/reports/room-service-sales",
    pdfEndpoint: "/reports/pdf/room-service-sales",
    columns: [],
  },
  {
    id: "room-revenue",
    title: "Room Revenue",
    category: "revenue",
    categoryLabel: "Revenue & Sales",
    filters: ["dateRange"],
    endpoint: "/reports/room-revenue",
    pdfEndpoint: "/reports/pdf/room-revenue",
    columns: [],
  },
  {
    id: "meal-breakdown",
    title: "Meal Breakdown",
    category: "revenue",
    categoryLabel: "Revenue & Sales",
    filters: ["dateRange"],
    endpoint: "/reports/meal-breakdown",
    pdfEndpoint: "/reports/pdf/meal-breakdown",
    columns: [],
  },
  {
    id: "monthly-revenue",
    title: "Monthly Revenue",
    category: "revenue",
    categoryLabel: "Revenue & Sales",
    filters: [],
    endpoint: "/operations/reports/monthly-revenue",
    pdfEndpoint: "",
    columns: [],
  },
  {
    id: "bookings-by-room-type",
    title: "Bookings by Room Type",
    category: "revenue",
    categoryLabel: "Revenue & Sales",
    filters: [],
    endpoint: "/operations/reports/bookings-by-room-type",
    pdfEndpoint: "",
    columns: [],
  },

  // === Services ===
  {
    id: "laundry",
    title: "Laundry",
    category: "services",
    categoryLabel: "Services",
    filters: ["dateRange", "staff"],
    endpoint: "/reports/laundry",
    pdfEndpoint: "/reports/pdf/laundry",
    columns: [],
  },
  {
    id: "ironing",
    title: "Ironing",
    category: "services",
    categoryLabel: "Services",
    filters: ["dateRange", "staff"],
    endpoint: "/reports/ironing",
    pdfEndpoint: "/reports/pdf/ironing",
    columns: [],
  },
  {
    id: "other-services",
    title: "Other Services",
    category: "services",
    categoryLabel: "Services",
    filters: ["dateRange"],
    endpoint: "/reports/other-services",
    pdfEndpoint: "/reports/pdf/other-services",
    columns: [],
  },
  {
    id: "car-rental",
    title: "Car Rental",
    category: "services",
    categoryLabel: "Services",
    filters: ["dateRange"],
    endpoint: "/reports/car-rental",
    pdfEndpoint: "/reports/pdf/car-rental",
    columns: [],
  },
  {
    id: "motorbike-rental",
    title: "Motorbike Rental",
    category: "services",
    categoryLabel: "Services",
    filters: ["dateRange"],
    endpoint: "/reports/motorbike-rental",
    pdfEndpoint: "/reports/pdf/motorbike-rental",
    columns: [],
  },
  {
    id: "car-wash",
    title: "Car Wash",
    category: "services",
    categoryLabel: "Services",
    filters: ["dateRange"],
    endpoint: "/reports/car-wash",
    pdfEndpoint: "/reports/pdf/car-wash",
    columns: [],
  },

  // === Payments & Financial ===
  {
    id: "cashier",
    title: "Cashier Report",
    category: "payments",
    categoryLabel: "Payments & Financial",
    filters: ["dateRange", "staff", "paymentMethod"],
    endpoint: "/reports/cashier",
    pdfEndpoint: "/reports/pdf/cashier",
    columns: [],
  },
  {
    id: "payments",
    title: "Payment Report",
    category: "payments",
    categoryLabel: "Payments & Financial",
    filters: ["dateRange", "paymentMethod"],
    endpoint: "/reports/payments",
    pdfEndpoint: "/reports/pdf/payments",
    columns: [],
  },
  {
    id: "outstanding-balances",
    title: "Outstanding Balances",
    category: "payments",
    categoryLabel: "Payments & Financial",
    filters: ["dateRange"],
    endpoint: "/reports/outstanding-balances",
    pdfEndpoint: "/reports/pdf/outstanding-balances",
    columns: [],
  },
  {
    id: "guest-folio",
    title: "Guest Folio",
    category: "payments",
    categoryLabel: "Payments & Financial",
    filters: ["bookingId"],
    endpoint: "/reports/guest-folio",
    pdfEndpoint: "/reports/pdf/guest-folio",
    columns: [],
  },
  {
    id: "weekly-statement",
    title: "Weekly Statement",
    category: "payments",
    categoryLabel: "Payments & Financial",
    filters: ["weekStart"],
    endpoint: "/reports/weekly-statement",
    pdfEndpoint: "/reports/pdf/weekly-statement",
    columns: [],
  },
  {
    id: "monthly-statement",
    title: "Monthly Statement",
    category: "payments",
    categoryLabel: "Payments & Financial",
    filters: ["month"],
    endpoint: "/reports/monthly-statement",
    pdfEndpoint: "/reports/pdf/monthly-statement",
    columns: [],
  },

  // === Tax & Compliance ===
  {
    id: "tax",
    title: "Tax / VAT Report",
    category: "tax",
    categoryLabel: "Tax & Compliance",
    filters: ["dateRange"],
    endpoint: "/reports/tax",
    pdfEndpoint: "/reports/pdf/tax",
    columns: [],
  },
  {
    id: "discounts",
    title: "Discount Report",
    category: "tax",
    categoryLabel: "Tax & Compliance",
    filters: ["dateRange"],
    endpoint: "/reports/discounts",
    pdfEndpoint: "/reports/pdf/discounts",
    columns: [],
  },
  {
    id: "void-cancelled",
    title: "Void / Cancelled Orders",
    category: "tax",
    categoryLabel: "Tax & Compliance",
    filters: ["dateRange"],
    endpoint: "/reports/void-cancelled",
    pdfEndpoint: "/reports/pdf/void-cancelled",
    columns: [],
  },

  // === Item Analytics ===
  {
    id: "item-sales",
    title: "Item Sales",
    category: "analytics",
    categoryLabel: "Item Analytics",
    filters: ["dateRange"],
    endpoint: "/reports/item-sales",
    pdfEndpoint: "/reports/pdf/item-sales",
    columns: [],
  },
  {
    id: "category-sales",
    title: "Category Sales",
    category: "analytics",
    categoryLabel: "Item Analytics",
    filters: ["dateRange"],
    endpoint: "/reports/category-sales",
    pdfEndpoint: "/reports/pdf/category-sales",
    columns: [],
  },
  {
    id: "top-guests",
    title: "Top 10 Guests",
    category: "analytics",
    categoryLabel: "Item Analytics",
    filters: [],
    endpoint: "/operations/reports/top-guests",
    pdfEndpoint: "",
    columns: [],
  },

  // === Income & Expense (general ledger) ===
  {
    id: "finance-profit-loss",
    title: "Profit & Loss",
    category: "finance",
    categoryLabel: "Income & Expense",
    filters: ["dateRange", "department"],
    endpoint: "/finance/profit-loss",
    pdfEndpoint: "/finance/pdf/profit-loss",
    columns: [],
  },
  {
    id: "finance-trial-balance",
    title: "Trial Balance",
    category: "finance",
    categoryLabel: "Income & Expense",
    filters: ["dateRange"],
    endpoint: "/finance/trial-balance",
    pdfEndpoint: "/finance/pdf/trial-balance",
    columns: [],
  },
  {
    id: "finance-day-book",
    title: "Day Book",
    category: "finance",
    categoryLabel: "Income & Expense",
    filters: ["date"],
    endpoint: "/finance/day-book",
    pdfEndpoint: "/finance/pdf/day-book",
    columns: [],
  },
  {
    id: "finance-cash-book",
    title: "Cash Book",
    category: "finance",
    categoryLabel: "Income & Expense",
    filters: ["dateRange"],
    endpoint: "/finance/cash-book",
    pdfEndpoint: "/finance/pdf/cash-book",
    columns: [],
  },
  {
    id: "finance-daily-cash",
    title: "Daily Cash Report",
    category: "finance",
    categoryLabel: "Income & Expense",
    filters: ["dateRange"],
    endpoint: "/finance/daily-cash",
    pdfEndpoint: "/finance/pdf/daily-cash",
    columns: [],
  },
  {
    id: "finance-daily-card",
    title: "Daily Card Report",
    category: "finance",
    categoryLabel: "Income & Expense",
    filters: ["dateRange"],
    endpoint: "/finance/daily-card",
    pdfEndpoint: "/finance/pdf/daily-card",
    columns: [],
  },
  {
    id: "finance-daily-bank",
    title: "Daily Bank Report",
    category: "finance",
    categoryLabel: "Income & Expense",
    filters: ["dateRange"],
    endpoint: "/finance/daily-bank",
    pdfEndpoint: "/finance/pdf/daily-bank",
    columns: [],
  },
  {
    id: "finance-reconciliation",
    title: "Source Reconciliation",
    category: "finance",
    categoryLabel: "Income & Expense",
    filters: ["date"],
    endpoint: "/finance/reconciliation",
    pdfEndpoint: "/finance/pdf/reconciliation",
    columns: [],
  },
  {
    id: "finance-room-performance",
    title: "Room Performance",
    category: "finance",
    categoryLabel: "Income & Expense",
    filters: ["dateRange"],
    endpoint: "/finance/room-performance",
    pdfEndpoint: "/finance/pdf/room-performance",
    columns: [],
  },
  {
    id: "finance-restaurant-performance",
    title: "Restaurant Performance",
    category: "finance",
    categoryLabel: "Income & Expense",
    filters: ["dateRange"],
    endpoint: "/finance/restaurant-performance",
    pdfEndpoint: "/finance/pdf/restaurant-performance",
    columns: [],
  },
  {
    id: "finance-stock-valuation",
    title: "Stock Valuation",
    category: "finance",
    categoryLabel: "Income & Expense",
    filters: ["date"],
    endpoint: "/finance/stock-valuation",
    pdfEndpoint: "/finance/pdf/stock-valuation",
    columns: [],
  },
  {
    id: "finance-food-cost",
    title: "Food Cost %",
    category: "finance",
    categoryLabel: "Income & Expense",
    filters: ["dateRange"],
    endpoint: "/finance/food-cost",
    pdfEndpoint: "/finance/pdf/food-cost",
    columns: [],
  },
  {
    id: "finance-purchase-register",
    title: "Purchase Register",
    category: "finance",
    categoryLabel: "Income & Expense",
    filters: ["dateRange"],
    endpoint: "/finance/purchase-register",
    pdfEndpoint: "/finance/pdf/purchase-register",
    columns: [],
  },
  {
    id: "finance-payroll-register",
    title: "Payroll Register",
    category: "finance",
    categoryLabel: "Income & Expense",
    filters: ["dateRange"],
    endpoint: "/finance/payroll/register",
    pdfEndpoint: "/finance/pdf/payroll-register",
    columns: [],
  },
  {
    id: "finance-ap-aging",
    title: "Payables Ageing",
    category: "finance",
    categoryLabel: "Income & Expense",
    filters: ["asOf", "termsDays"],
    endpoint: "/finance/aging/payables",
    pdfEndpoint: "/finance/pdf/ap-aging",
    columns: [],
  },
  {
    id: "finance-ar-aging",
    title: "Receivables Ageing",
    category: "finance",
    categoryLabel: "Income & Expense",
    filters: ["asOf", "termsDays"],
    endpoint: "/finance/aging/receivables",
    pdfEndpoint: "/finance/pdf/ar-aging",
    columns: [],
  },
  {
    id: "finance-annual-pnl",
    title: "Annual Profit & Loss",
    category: "finance",
    categoryLabel: "Income & Expense",
    filters: ["year", "department"],
    endpoint: "/finance/analytics",
    pdfEndpoint: "/finance/pdf/annual-pnl",
    columns: [],
  },
];

export const REPORT_CATEGORIES: Record<ReportCategory, { label: string; reports: ReportDefinition[] }> = REPORT_REGISTRY.reduce(
  (acc, report) => {
    if (!acc[report.category]) {
      acc[report.category] = { label: report.categoryLabel, reports: [] };
    }
    acc[report.category].reports.push(report);
    return acc;
  },
  {} as Record<ReportCategory, { label: string; reports: ReportDefinition[] }>,
);
